/**
 * <gsd-board> — a product page for a board of one-of-a-kind pins.
 * The board photo carries a tap target for every pin. Tapping a pin selects it and opens a panel with its price and an
 * Add to cart button (a bottom sheet on phones). Pins already in the cart and pins that are sold are marked on the photo.
 *
 * Each pin is a variant named "Pin N". The add to cart itself is handled by gsd-cart.js (the form carries data-gsd-add).
 */
class GsdBoard extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.pins = Array.from(this.querySelectorAll('.gsd-board__pin'));
    this.panel = this.querySelector('[data-panel]');
    if (!this.pins.length || !this.panel) return;
    this.ready = true;

    this.selectedBlock = this.panel.querySelector('[data-selected]');
    this.titleEl = this.panel.querySelector('[data-panel-title]');
    this.priceEl = this.panel.querySelector('[data-panel-price]');
    this.variantInput = this.panel.querySelector('[data-variant-id]');
    this.addButton = this.panel.querySelector('[data-add-button]');
    this.addLabel = this.panel.querySelector('[data-gsd-label]');
    this.viewCart = this.panel.querySelector('[data-view-cart]');
    this.countNumber = this.querySelector('[data-count-number]');
    this.inCartLabel = this.dataset.inCartLabel || 'In your cart';
    this.addLabelText = this.dataset.addLabel || 'Add to cart';

    this.selected = null;
    this.inCart = new Set();

    this.pins.forEach((pin) => pin.addEventListener('click', () => this.toggle(pin)));
    this.panel.querySelector('[data-close]')?.addEventListener('click', () => this.deselect());
    this.onKey = (event) => {
      if (event.key === 'Escape' && this.selected) this.deselect(true);
    };
    document.addEventListener('keydown', this.onKey);

    // After gsd-cart.js adds a pin, mark it
    this.onAdded = (event) => {
      const id = String(event.detail?.variant_id || event.detail?.id || '');
      if (id) this.inCart.add(id);
      this.refreshMarks();
    };
    document.addEventListener('gsd:cart-added', this.onAdded);

    this.onVisible = () => {
      if (document.visibilityState === 'visible') this.refresh();
    };
    document.addEventListener('visibilitychange', this.onVisible);
    window.addEventListener('pageshow', this.onVisible);

    this.refresh();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('gsd:cart-added', this.onAdded);
    document.removeEventListener('visibilitychange', this.onVisible);
    window.removeEventListener('pageshow', this.onVisible);
    this.ready = false;
  }

  /** Sync with the cart and the stock of the product (another shopper may have taken a pin). */
  async refresh() {
    try {
      const [cart, product] = await Promise.all([
        fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json()),
        fetch(`/products/${this.dataset.handle}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json()),
      ]);
      this.inCart = new Set(cart.items.map((item) => String(item.variant_id)));
      const available = new Map(product.variants.map((variant) => [String(variant.id), variant.available]));
      this.pins.forEach((pin) => {
        const isAvailable = available.get(pin.dataset.variant);
        if (isAvailable === undefined) return;
        pin.classList.toggle('is-sold', !isAvailable);
        pin.disabled = !isAvailable;
      });
      if (this.countNumber) {
        this.countNumber.textContent = String(product.variants.filter((variant) => variant.available).length);
      }
      if (this.selected?.disabled) this.deselect();
      this.refreshMarks();
    } catch (error) {
      /* Offline or blocked: the page still works from what it was rendered with */
    }
  }

  refreshMarks() {
    this.pins.forEach((pin) => pin.classList.toggle('is-in-cart', this.inCart.has(pin.dataset.variant)));
    this.updatePanel();
  }

  toggle(pin) {
    if (pin.disabled) return;
    if (this.selected === pin) this.deselect();
    else this.select(pin);
  }

  select(pin) {
    if (this.selected) {
      this.selected.classList.remove('is-selected');
      this.selected.setAttribute('aria-pressed', 'false');
    }
    this.selected = pin;
    pin.classList.add('is-selected');
    pin.setAttribute('aria-pressed', 'true');
    this.classList.add('has-pin');
    this.panel.classList.add('has-pin');
    this.selectedBlock.hidden = false;
    this.titleEl.textContent = pin.dataset.name;
    this.priceEl.textContent = pin.dataset.price;
    this.variantInput.value = pin.dataset.variant;
    this.updatePanel();
    this.keepPinInView(pin);
  }

  deselect(returnFocus = false) {
    const pin = this.selected;
    if (pin) {
      pin.classList.remove('is-selected');
      pin.setAttribute('aria-pressed', 'false');
      if (returnFocus) pin.focus();
    }
    this.selected = null;
    this.classList.remove('has-pin');
    this.panel.classList.remove('has-pin');
    this.selectedBlock.hidden = true;
  }

  /** The button says "In your cart" (and is off) for a pin that is already in the cart. */
  updatePanel() {
    if (!this.selected) return;
    const taken = this.inCart.has(this.selected.dataset.variant);
    this.addButton.disabled = taken;
    if (this.addLabel) this.addLabel.textContent = taken ? this.inCartLabel : this.addLabelText;
    if (this.viewCart) this.viewCart.hidden = !taken;
  }

  /** On phones the sheet covers the bottom of the screen: bring a pin that would be hidden behind it back into view. */
  keepPinInView(pin) {
    if (!window.matchMedia('(max-width: 989px)').matches) return;
    requestAnimationFrame(() => {
      const sheetTop = window.innerHeight - this.panel.offsetHeight - 24;
      const rect = pin.getBoundingClientRect();
      const headerRoom = 90;
      let shift = 0;
      if (rect.bottom + 48 > sheetTop) shift = rect.bottom + 48 - sheetTop;
      else if (rect.top < headerRoom) shift = rect.top - headerRoom;
      if (shift) window.scrollBy({ top: shift, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    });
  }
}

if (!customElements.get('gsd-board')) customElements.define('gsd-board', GsdBoard);
